import { Paper, useTheme } from "@mui/material";
import { type FC, useState } from "react";
import type { PbjPrediction } from "../../../utils/models";
import { cardShadow, PBJ_RADIUS } from "./colors";
import type { PbjDraftControls } from "./pbjDraftControls";
import { RecommendationLeverList } from "./RecommendationLeverList";
import { RecommendationsFooter } from "./RecommendationsFooter";
import { RecommendationsHeader } from "./RecommendationsHeader";
import type { PlanItem } from "./recommendations.constants";
import type { TrackerButtonState } from "./trackedPlan.utils";
import { useRecommendationSliders } from "./useRecommendationSliders";
import { useSuggestPlanClick } from "./useSuggestPlanClick";
import { WageOverridesDialog } from "./WageOverridesDialog";

// "qtd" = catch-up over remaining-from-today days; "quarter" = sustained across the
// full calendar quarter. Lifted to the parent so ActionItems uses the same mode.
export type PeriodMode = "qtd" | "quarter";

type Props = {
	prediction: PbjPrediction;
	census: number;
	// Live config draft + persist/recompute callbacks (one grouped prop shared with the
	// hero + breakdown table). The wage-overrides dialog seeds from `draft` and, on Save,
	// both writes the draft and recalculates with one click (like the census +
	// manual-turnover dialogs).
	draftControls: PbjDraftControls;
	// Full plan items (not just ids) so sliders + impact/points labels can reflect
	// the BE-suggested values verbatim when an action is in the plan.
	selectedPlan: PlanItem[];
	onAddToPlan: (item: PlanItem) => void;
	onUpdatePlan: (item: PlanItem) => void;
	// Drops a lever from the Action Plan — used when a drag leaves an in-plan cut under one whole hour.
	onRemoveFromPlan: (id: PlanItem["id"]) => void;
	// Reset the Action Plan back to the pre-computed suggestion (the post-refresh default).
	// Paired with re-seeding the sliders locally so "Reset plan" restores the whole section.
	onResetPlan: () => void;
	// Disable the button when the greedy returned no steps (no wage data, the target is met,
	// or no available addition can advance it).
	canSuggest: boolean;
	periodMode: PeriodMode;
	onPeriodModeChange: (next: PeriodMode) => void;
	// Committed ISO date range — the window the suggested-plan endpoint re-optimizes
	// over when there are locked (manually-added) buckets in the plan.
	startDate: string;
	endDate: string;
	// "Add to tracker" — the whole assembled Action Plan, not one recommendation. The payload is
	// built in ScoringAndRecommendations (which owns selectedPlan + the resolved target); this
	// panel only renders the state and reports the click.
	trackerState: TrackerButtonState;
	onTrackPlan: () => void;
	isTrackingPlan: boolean;
};

/**
 * The "How" panel: a header (period toggle, wage gear, Suggest / Reset), one slider card per lever,
 * and a footer that commits the sliders to the Action Plan and the plan to the tracker. Slider
 * state lives in `useRecommendationSliders`; Suggest lives in `useSuggestPlanClick`.
 */
export const RecommendationsSidebar: FC<Props> = ({
	prediction,
	census,
	draftControls,
	selectedPlan,
	onAddToPlan,
	onUpdatePlan,
	onRemoveFromPlan,
	onResetPlan,
	canSuggest,
	periodMode,
	onPeriodModeChange,
	startDate,
	endDate,
	trackerState,
	onTrackPlan,
	isTrackingPlan,
}) => {
	const theme = useTheme();
	// Owned here rather than in the header: the dialog is now opened from each lever's wage ⓘ (the
	// figures it changes sit on the lever cards), so the one surface that contains both is this panel.
	const [wageOverridesOpen, setWageOverridesOpen] = useState(false);
	const sliders = useRecommendationSliders({ prediction, census, selectedPlan, onAddToPlan, onUpdatePlan, onRemoveFromPlan, onResetPlan });
	const { handleSuggestClick, isSuggesting } = useSuggestPlanClick({
		prediction,
		census,
		selectedPlan,
		startDate,
		endDate,
		visibleRecs: sliders.visibleRecs,
		onApply: sliders.applySuggestedPlan,
	});

	return (
		<Paper
			elevation={0}
			sx={{ borderRadius: PBJ_RADIUS.CARD, overflow: "hidden", boxShadow: cardShadow(theme), height: "100%", display: "flex", flexDirection: "column" }}
			data-testid="pbj-model-recommendations"
		>
			<RecommendationsHeader
				anyCuttable={sliders.anyCuttable}
				periodMode={periodMode}
				onPeriodModeChange={onPeriodModeChange}
				canSuggest={canSuggest}
				isSuggesting={isSuggesting}
				onSuggest={handleSuggestClick}
				onReset={sliders.handleReset}
			/>
			<RecommendationLeverList
				prediction={prediction}
				census={census}
				visibleRecs={sliders.visibleRecs}
				selectedById={sliders.selectedById}
				spendMap={sliders.spendMap}
				suggestedRecIds={sliders.suggestedRecIds}
				suggestNonce={sliders.suggestNonce}
				selectedPlan={selectedPlan}
				periodMode={periodMode}
				draft={draftControls.draft}
				onAdd={sliders.handleAddLever}
				onSlider={sliders.handleSliderChange}
				onEditWages={() => setWageOverridesOpen(true)}
			/>
			{sliders.visibleRecs.length > 0 && (
				<RecommendationsFooter
					valuedCount={sliders.valuedRecs.length}
					onAddAll={sliders.handleAddAll}
					planSize={selectedPlan.length}
					trackerState={trackerState}
					onTrackPlan={onTrackPlan}
					isTrackingPlan={isTrackingPlan}
				/>
			)}
			<WageOverridesDialog
				open={wageOverridesOpen}
				onClose={() => setWageOverridesOpen(false)}
				prediction={prediction}
				draft={draftControls.draft}
				onDraftChange={draftControls.onDraftChange}
				onCalculate={draftControls.onCalculate}
			/>
		</Paper>
	);
};
